// ADR 27.10.26e — the cookie that only says "signed in".
//
// The session lives in app.getlingara.com's `localStorage`, which the marketing
// site cannot read. This one boolean, on the shared registrable domain, is what
// lets getlingara.com keep its chat invite away from a learner who is signed in
// (Public 27.10.26f). It carries no token, no user id and no tier: a forged or
// stale value can only make the site quieter (§3).

/** The cookie's name. Its value is always the literal `1`. */
export const SIGNED_IN_HINT_COOKIE = 'lingara_signed_in'

/** The registrable domain both the app and the marketing site live under. */
export const SIGNED_IN_HINT_DOMAIN = 'getlingara.com'

// 30 days, renewed by every `storeSession` — sign-in, boot refresh and every
// token refresh. WebKit caps a script-written cookie at 7 days; §1 accepts that.
const MAX_AGE_SECONDS = 30 * 86400

/** The one property of `Document` this module touches, so a test can fake it. */
export interface CookieJar {
  cookie: string
}

// On `localhost` and `*.pages.dev` previews a `Domain=getlingara.com` cookie
// would be rejected anyway; the guard makes that explicit, and keeps every jsdom
// suite that calls `storeSession` free of a side effect.
function onHintDomain(hostname: string): boolean {
  return hostname === SIGNED_IN_HINT_DOMAIN || hostname.endsWith(`.${SIGNED_IN_HINT_DOMAIN}`)
}

function writeHint(doc: CookieJar, hostname: string, maxAge: number): void {
  if (!onHintDomain(hostname)) return
  doc.cookie =
    `${SIGNED_IN_HINT_COOKIE}=1; Domain=${SIGNED_IN_HINT_DOMAIN}; Path=/; ` +
    `Max-Age=${maxAge}; Secure; SameSite=Lax`
}

/** Write (or renew) the hint. A no-op off getlingara.com. */
export function setSignedInHint(doc: CookieJar, hostname: string): void {
  writeHint(doc, hostname, MAX_AGE_SECONDS)
}

/** Expire the hint with the same attributes. A no-op off getlingara.com. */
export function clearSignedInHint(doc: CookieJar, hostname: string): void {
  writeHint(doc, hostname, 0)
}
